import {Dropdown, Input, Locale, SelectOptionType, TextArea} from '@local/ui/components'
import {defaultLocale, otherLocale} from '@local/config/locales.mjs'
import {cx} from '@local/utils/cx'
import {slugify} from '@local/utils/string'
import {parseLocale} from '@local/utils/locale'
import {useTranslate} from '@local/hooks'
import {useMemo} from 'react'
import type {RetreatOptionWithDescription, RetreatQuestion} from './types'

const LOCALE_KEY_SKIP = new Set(['_type', 'fallbackToDefault'])

function resolveOptionalLocalizedText (value: unknown): string {
	if (value == null) {
		return ''
	}
	if (typeof value === 'string') {
		return value.trim()
	}
	if (typeof value !== 'object' || Array.isArray(value)) {
		return ''
	}
	const record = value as Record<string, unknown>
	const keys = Object.keys(record).filter(k => !LOCALE_KEY_SKIP.has(k))
	const allLocaleKeyed = keys.length > 0 && keys.every(k => /^[a-z]{2}_[a-z]{2}$/i.test(k))
	if (!allLocaleKeyed || !defaultLocale) {
		return ''
	}
	const defaultKey = parseLocale.dashToUnderscore(defaultLocale.id)
	const defaultVal = record[defaultKey]
	if (typeof defaultVal === 'string' && defaultVal.trim()) {
		return defaultVal.trim()
	}
	for (const loc of otherLocale) {
		const key = parseLocale.dashToUnderscore(loc.id)
		const v = record[key]
		if (typeof v === 'string' && v.trim()) {
			return v.trim()
		}
	}
	return ''
}

const generateMonthOptions = (): Array<SelectOptionType> => {
	const today = new Date()
	const months: Array<SelectOptionType> = []

	for (let i = 1; i <= 12; i++) {
		let year = today.getFullYear()
		let month = today.getMonth() + i
		if (month > 11) {
			year++
			month -= 12
		}
		const date = new Date(year, month, 1)
		const monthLabel = date.toLocaleString('default', {month: 'long'})
		months.push({
			label: `${monthLabel} ${year}`,
			value: `${year}-${(month + 1).toString().padStart(2, '0')}`,
		})
	}

	return months
}

function gridClassForType (inputType: RetreatQuestion['inputType']): string {
	switch (inputType) {
		case 'months':
			return 'grid grid-cols-3 gap-10'
		case 'radio':
			return 'grid grid-cols-2 gap-10'
		case 'radioWithDescription':
			return 'grid grid-cols-1 gap-10'
		case 'checkbox':
			return 'grid grid-cols-3 gap-10'
		default:
			return ''
	}
}

export interface RetreatQuestionFieldProps {
	question: RetreatQuestion
	showError: boolean
}

export const RetreatQuestionField = ({question, showError}: RetreatQuestionFieldProps) => {
	const locale = useTranslate()
	const {title, inputType, required, description, placeholder} = question
	const name = question.fieldKey
	const options = useMemo(() => {
		if (Array.isArray(question.options)) {
			return question.options
		}
		if (question.options && typeof question.options === 'object') {
			const localized = Object.values(question.options as Record<string, unknown>).find(Array.isArray)
			if (Array.isArray(localized)) {
				return localized.map(String)
			}
		}
		return []
	}, [question.options])
	const optionsWithDescription = useMemo((): Array<RetreatOptionWithDescription> => {
		const raw = question.optionsWithDescription
		if (Array.isArray(raw)) {
			return raw
		}
		if (raw && typeof raw === 'object') {
			const localized = Object.values(raw as Record<string, unknown>).find(Array.isArray)
			if (Array.isArray(localized)) {
				return localized as Array<RetreatOptionWithDescription>
			}
		}
		return []
	}, [question.optionsWithDescription])

	const monthOptions = useMemo(() => generateMonthOptions(), [])
	const optionItems = useMemo(
		() => options.map((label) => ({label, value: slugify(label)})),
		[options],
	)

	const descriptionText = useMemo(
		() => resolveOptionalLocalizedText(description),
		[description],
	)
	const optionItemsWithDescription = useMemo(
		() =>
			optionsWithDescription
				.map((option) => {
					const titleText = resolveOptionalLocalizedText(option.title)
					if (!titleText) {
						return null
					}
					return {
						_key: option._key,
						title: titleText,
						description: resolveOptionalLocalizedText(option.description),
						value: slugify(titleText),
					}
				})
				.filter((option): option is {_key?: string; title: string; description: string; value: string} => option !== null),
		[optionsWithDescription],
	)

	const selectOneMessage = locale('Select one option')
	const selectManyMessage = locale('Select at least one option')

	const errorHint =
		inputType === 'radio' || inputType === 'radioWithDescription' || inputType === 'dropdown'
			? selectOneMessage
			: selectManyMessage

	if (!name) {
		return null
	}

	return (
		<div className='relative flex flex-col gap-20'>
			<div className='flex flex-col gap-12'>
				<h3>
					{title}
				</h3>
				{descriptionText !== '' && (
					<div className='max-w-prose' role='note'>
						<p className='text-12 text-grey-1 leading-relaxed whitespace-pre-line'>
							{descriptionText}
						</p>
					</div>
				)}
			</div>
			{(inputType === 'checkbox' || inputType === 'months' || inputType === 'radio' || inputType === 'radioWithDescription') && (
				<span
					className={cx(
						'text-12 text-red absolute top-28',
						'opacity-0 transition-opacity duration-150',
						showError && 'opacity-100',
					)}>
					<Locale>{errorHint}</Locale>
				</span>
			)}
			{(inputType === 'radio' || inputType === 'checkbox') && (
				<div className={gridClassForType(inputType)}>
					{optionItems.map((option) => (
						<label key={`${name}-${option.value}`} htmlFor={`${name}-${option.value}`}>
							<input
								type={inputType === 'radio' ? 'radio' : 'checkbox'}
								name={name}
								value={option.value}
								id={`${name}-${option.value}`}
								className='sr-only peer' />
							<span
								className={cx(
									'bg-transparent flex items-center justify-center h-48 text-12',
									'rounded-xxs border border-grey-2 peer-focus-visible:border-grey-1',
									'peer-checked:border-white transition-colors duration-200 ease-quart-out hover:cursor-pointer',
									inputType === 'checkbox' &&
										'min-h-[3rem] h-full text-center py-10 px-5',
								)}>
								{option.label}
							</span>
						</label>
					))}
				</div>
			)}
			{inputType === 'radioWithDescription' && (
				<div className={gridClassForType('radioWithDescription')}>
					{optionItemsWithDescription.map((option, index) => {
						const rowKey = option._key ?? `${name}-${option.value}-${index}`
						const controlId = `${name}-rwd-${option._key ?? index}-${option.value}`
						return (
							<label key={rowKey} htmlFor={controlId}>
								<input
									type='radio'
									name={name}
									value={option.value}
									id={controlId}
									className='sr-only peer' />
								<span
									className={cx(
										'bg-transparent flex gap-8 items-start h-auto min-h-[3rem] py-10 px-5 text-left text-12',
										'rounded-xxs border border-grey-2 peer-focus-visible:border-grey-1',
										'peer-checked:border-white transition-colors duration-200 ease-quart-out hover:cursor-pointer',
									)}>
									<span aria-hidden className='mt-1 shrink-0'>•</span>
									<span className='flex flex-col gap-6'>
										<span className='leading-snug'>{option.title}</span>
										{option.description !== '' && (
											<span className='text-12 text-grey-1 leading-relaxed'>
												{option.description}
											</span>
										)}
									</span>
								</span>
							</label>
						)
					})}
				</div>
			)}
			{inputType === 'months' && (
				<div className={gridClassForType('months')}>
					{monthOptions.map((option) => (
						<label key={option.value} htmlFor={`${name}-${option.value}`}>
							<input
								type='checkbox'
								name={name}
								value={option.value}
								id={`${name}-${option.value}`}
								className='sr-only peer' />
							<span
								className={cx(
									'bg-transparent flex items-center justify-center h-48 text-12',
									'rounded-xxs border border-grey-2 peer-focus-visible:border-grey-1',
									'peer-checked:border-white transition-colors duration-200 ease-quart-out hover:cursor-pointer',
									'peer-invalid:border-red',
								)}>
								{option.label}
							</span>
						</label>
					))}
				</div>
			)}
			{inputType === 'dropdown' && (
				<>
					<span
						className={cx(
							'text-12 text-red absolute top-28',
							'opacity-0 transition-opacity duration-150',
							showError && 'opacity-100',
						)}>
						<Locale>{selectOneMessage}</Locale>
					</span>
					<Dropdown
						id={name}
						name={name}
						placeholder={placeholder || locale('Select an option')}
						options={optionItems.map((o) => ({label: o.label, value: o.value}))} />
				</>
			)}
			{inputType === 'text' && (
				<div className='grid grid-cols-1 gap-10'>
					<Input
						required={required}
						variant='dark'
						type='text'
						name={name}
						label={placeholder || title}
						className='h-48'
						inputClassName='h-full'
						errorClassName='z-10 bg-grey-4'
						labelClassName='bg-grey-4' />
				</div>
			)}
			{inputType === 'textarea' && (
				<TextArea
					required={required}
					variant='dark'
					name={name}
					label={placeholder || title}
					inputClassName='h-full'
					className='h-72 w-full'
					errorClassName='z-10 bg-grey-4'
					labelClassName='bg-grey-4' />
			)}
		</div>
	)
}
